
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'

// @components
import NewModal from '@/common/new-components/NewModal/index.vue'
import NewSelect from '@/ui-kit/NewSelect/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'
import NewInput from '@/ui-kit/NewInput/index.vue'
import AddCarModal from '@/modules/TMSModule/components/AddCarModal/index.vue'

// @types
import { DispatcherSectorT } from '@/core/types/dispatchers_sector.types'
import { CarrierT } from '@/core/types/carriers.types'
import { CouirerCarT, CreateCouirerCar, CourierTypeT } from '@/core/types/couriers.types'

// @services
import { carriers } from '@/core/realisations/carriers.realisation'
import { dispatchers_sectors } from '@/core/realisations/dispatchers_sectors.realisation'
import { couriers } from '@/core/realisations/couriers.realisation'
import { security } from '@/core/realisations/security.realisation'

// @validation
import useVuelidate from '@vuelidate/core'
import { required, minLength } from '@vuelidate/validators'

// @composable
import { useMaska } from '@/common/composable/useMaska'
import { useFormData } from '@/common/composable/useFormData'
import useNotifications from '@/common/composable/useNotifications'

// @constants
import { PHONE_NUMBER_MASK } from '@/core/constants/masks.constants'
import { CourierPaymentTypes } from '@/core/constants/couriers.constants'

export default defineComponent({
    components: {
        NewModal,
        NewSelect,
        NewButton,
        FileReader,
        NewInput,
        AddCarModal,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        ftlType: {
            type: Number,
            default: null,
        },
    },
    emits: ['create', 'cancel', 'set-car'],
    setup(props, { emit }) {
        const { unmaskNumbers } = useMaska()
        const notifications = useNotifications()

        const state = reactive({
            iin: null as string | null,
            dispatcherSectorId: null as number | null,
            companyId: null as number | null,
            fullName: null as string | null,
            phoneNumber: null as string | null,
            password: null as string | null,
            certificate: [] as File[],
            driverLicenses: [] as File[],
            courierLicenses: {
                identifyCardNumber: null as string | null,
                identifyCardIssueDate: null as string | null,
                driverLicenseNumber: null as string | null,
                driverLicenseIssueDate: null as string | null,
            },
            //
            carId: null as number | null,
            typeIds: [] as number[],
            // hardcode
            isActive: 1,
            paymentRateType: 2,
            paymentAmount: 0,
        })

        const car_state = reactive({
            is_opened: false,
            is_loading: false,
        })

        const CourierCreateValidation = {
            iin: { required },
            dispatcherSectorId: { required },
            companyId: { required },
            fullName: { required },
            phoneNumber: { required },
            carId: { required },
            courierLicenses: {
                identifyCardNumber: { required },
                identifyCardIssueDate: { required },
                driverLicenseNumber: { required },
                driverLicenseIssueDate: { required },
            },
            typeIds: { required },
        }

        const state_form$ = useVuelidate(CourierCreateValidation, state)
        const iin_form$ = useVuelidate(
            { iin: { required, minLength: minLength(props.ftlType === 1 ? 0 : 12) } },
            state
        )

        const sb_state_iin = reactive({
            is_checked: false,
            is_rejected: null as boolean | null,
            is_loading: false,
            reject_comment: null as string | null,
            is_request_completed: false,
            backend_error: null as string | null,
            //
            is_success: false,
        })

        //

        const resetSbStateIIN = () => {
            sb_state_iin.is_checked = false
            sb_state_iin.is_rejected = null
            sb_state_iin.reject_comment = null
            sb_state_iin.is_request_completed = false
            sb_state_iin.is_success = false
        }

        const isValidIIN = computed(() => {
            const iinLength = props.ftlType === 1 ? 0 : 12
            return !sb_state_iin.is_loading && state.iin && state.iin.length >= iinLength
        })

        const checkSbStatusIIN = async () => {
            iin_form$.value.$touch()
            if (iin_form$.value.$invalid || !isValidIIN.value) return

            resetSbStateIIN()

            try {
                sb_state_iin.is_loading = true

                const PERSON_CHECKUP_TYPE = 2

                const response: any = await security.getCheckupStatus({
                    identifier: state.iin as string,
                    type: PERSON_CHECKUP_TYPE,
                    checkInSystem: 1,
                })

                if (!response.success) {
                    notifications.notify({
                        title: response.message || 'При проверке произошла ошибка',
                        type: 'danger',
                    })
                }
                sb_state_iin.is_checked = true

                if (response.data.isAccepted === false) {
                    sb_state_iin.is_rejected = true
                    sb_state_iin.reject_comment = response.data.comment
                } else if (response.data.isAccepted === true) {
                    sb_state_iin.is_success = true
                    sb_state_iin.is_rejected = false
                }

                if (response.message) {
                    sb_state_iin.backend_error = response.message
                }
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При проверке статуса СБ произошла ошибка',
                })
                sb_state_iin.is_checked = false
            } finally {
                sb_state_iin.is_loading = false
                sb_state_iin.is_request_completed = true
            }
        }

        //

        const emitCancel = () => {
            emit('cancel')
        }

        const emitCreate = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (!sb_state_iin.is_success) return

            const form_data = new FormData()

            form_data.append('fullName', state.fullName as string)
            form_data.append('phoneNumber', unmaskNumbers(state.phoneNumber as string))
            form_data.append('password', state.password as string)
            form_data.append('dispatcherSectorId', (state.dispatcherSectorId as number).toString())
            form_data.append('carId', (state.carId as number).toString())
            form_data.append('isActive', '1')
            form_data.append('paymentRateType', (state.paymentRateType as number).toString())
            form_data.append('paymentAmount', state.paymentAmount.toString())
            form_data.append('companyId', (state.companyId as number).toString())
            form_data.append('iin', state.iin ? state.iin : '')
            form_data.append('courierLicenses', JSON.stringify(state.courierLicenses))
            form_data.append('typeIds', JSON.stringify(state.typeIds))

            if (state.certificate.length) {
                state.certificate.forEach((certificate) => {
                    form_data.append('identificationCards[]', certificate)
                })
            }

            if (state.driverLicenses.length) {
                state.driverLicenses.forEach((driver_license) => {
                    form_data.append('driverLicenses[]', driver_license)
                })
            }

            emit('create', form_data)
            return
        }

        const data_state = reactive({
            dispatchers_sectors: {
                is_loading: false,
                is_failed: false,
                list: [] as DispatcherSectorT[],
                pagination: {
                    current_page: 1,
                    total_pages: 1,
                    limit: null as number | null,
                    total_items: null as number | null,
                },
            },
            companies: {
                is_loading: false,
                is_failed: false,
                list: [] as CarrierT[],
                pagination: {
                    current_page: 1,
                    total_pages: 1,
                    limit: null as number | null,
                    total_items: null as number | null,
                },
            },
            cars: {
                is_loading: false,
                is_failed: false,
                list: [] as CouirerCarT[],
                pagination: {
                    current_page: 1,
                    total_pages: 1,
                    limit: null as number | null,
                    total_items: null as number | null,
                },
            },
            types: {
                is_loading: false,
                is_failed: false,
                list: [] as CourierTypeT[],
                pagination: {
                    current_page: 1,
                    total_pages: 1,
                    limit: null as number | null,
                    total_items: null as number | null,
                },
            },
        })

        const loadDispatchersSectors = async () => {
            try {
                data_state.dispatchers_sectors.is_loading = true

                const response = await dispatchers_sectors.getList()
                data_state.dispatchers_sectors.list = response.data

                data_state.dispatchers_sectors.is_failed = false
            } catch (error) {
                data_state.dispatchers_sectors.is_failed = true
            } finally {
                data_state.dispatchers_sectors.is_loading = false
            }
        }

        const loadCourierTypes = async () => {
            try {
                data_state.types.is_loading = true

                const response = await couriers.getCourierTypes()
                data_state.types.list = response.data

                data_state.types.is_failed = false
            } catch (error) {
                data_state.types.is_failed = true
            } finally {
                data_state.types.is_loading = false
            }
        }

        const toggleCar = () => {
            car_state.is_opened = !car_state.is_opened
        }

        const setCar = (response: CreateCouirerCar, files: File[]) => {
            state.carId = response.id
            emit('set-car', { ...response }, files)
        }

        const createCar = async (payload: FormData, files: File[]) => {
            try {
                car_state.is_loading = true

                const response = await couriers.createCar(payload)

                await loadCars(1)
                toggleCar()
                setCar(response.data, files)
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При создании автомобиля произошла ошибка',
                })
            } finally {
                car_state.is_loading = false
            }
        }

        //

        const loadCarriers = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                const params = {
                    page,
                    search,
                    getAllCarriers: 1,
                    limit: 9999,
                    statusId: 2,
                }

                data_state.companies.is_loading = true

                const response = await carriers.getList(params)

                data_state.companies.list = response.data
                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
            } catch (error) {
                data_state.companies.is_failed = true
            } finally {
                data_state.companies.is_loading = false
            }
        }

        const loadMoreCarriers = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                const params = {
                    page,
                    getAllCarriers: 1,
                    limit: 9999,
                    statusId: 2,
                }

                data_state.companies.is_loading = true

                const response = await carriers.getList(params)
                data_state.companies.list = data_state.companies.list.concat(response.data)
                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.is_failed = false
            } catch (error) {
                data_state.companies.is_failed = true
            } finally {
                data_state.companies.is_loading = false
            }
        }

        const searchCarriers = (search_query: string) => {
            loadCarriers(1, search_query)
        }

        const unshiftFindedCarrier = async (company: CarrierT) => {
            await loadCarriers(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        // Автомобили

        const loadCars = async (page: number, search: string | null = null) => {
            data_state.cars.pagination.current_page = page

            try {
                data_state.cars.is_loading = true

                const response = await couriers.getCars({
                    page,
                    number: search,
                })

                data_state.cars.list = response.data
                data_state.cars.pagination.total_pages = response.meta.last_page

                data_state.cars.is_failed = false
            } catch (error) {
                data_state.cars.is_failed = true
            } finally {
                data_state.cars.is_loading = false
            }
        }

        const loadMoreCars = async (page: number) => {
            if (page > data_state.cars.pagination.total_pages) return
            data_state.cars.pagination.current_page = page

            try {
                data_state.cars.is_loading = true

                const response = await couriers.getCars({
                    page,
                })
                data_state.cars.list = data_state.cars.list.concat(response.data)
                data_state.cars.pagination.total_pages = response.meta.last_page

                data_state.cars.is_failed = false
                data_state.cars.is_failed = false
            } catch (error) {
                data_state.cars.is_failed = true
            } finally {
                data_state.cars.is_loading = false
            }
        }

        const searchCars = (search_query: string) => {
            loadCars(1, search_query)
        }

        const unshiftFindedCar = async (car: CouirerCarT) => {
            await loadCars(data_state.cars.pagination.current_page)
            data_state.cars.list.unshift(car)
        }

        // computed IIN

        const isNotFoundIIN = computed(
            () => sb_state_iin.is_rejected === null && sb_state_iin.is_request_completed
        )
        const isRejectedIIN = computed(
            () => sb_state_iin.is_rejected && sb_state_iin.is_request_completed
        )
        const isNotCheckedIIN = computed(
            () => !sb_state_iin.is_checked && sb_state_iin.is_request_completed
        )

        //

        onBeforeMount(() => {
            loadDispatchersSectors()
            loadCarriers(1)
            loadCars(1)
            loadCourierTypes()
        })

        return {
            state,
            car_state,
            data_state,
            sb_state_iin,
            //
            state_form$,
            iin_form$,
            //
            loadDispatchersSectors,
            loadCourierTypes,
            toggleCar,
            createCar,
            checkSbStatusIIN,
            //
            loadCarriers,
            loadMoreCarriers,
            searchCarriers,
            unshiftFindedCarrier,
            //
            loadCars,
            loadMoreCars,
            searchCars,
            unshiftFindedCar,
            //
            emitCancel,
            emitCreate,
            //
            PHONE_NUMBER_MASK,
            CourierPaymentTypes,
            // iin computed
            isRejectedIIN,
            isNotCheckedIIN,
            isNotFoundIIN,
        }
    },
})
